import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideEnvironmentInitializer,
} from '@angular/core';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, UrlSerializer } from '@angular/router';

import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SORT_HEADER_INTL_PROVIDER } from '@angular/material/sort';

import { lastValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { provideNgxMask } from 'ngx-mask';

import { provideLoadingBarInterceptor } from '@ngx-loading-bar/http-client';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { authInterceptor } from 'src/app/http-auth.interceptor';
import { AuthService } from 'src/app/auth.service';
import { ConfigurationService } from 'src/app/configuration.service';
import { IconRepositoryService } from 'src/app/icon-repository.service';
import { routes } from 'src/app/app.routes';
import { TessUrlSerializer } from 'src/app/tess-url-serializer';

import { AuthStore } from '@core/auth/models/auth-store';
import { SHARED_ASSETS_PATH } from '@tess-core/shared';

export const appInit = (configurationService: ConfigurationService, authService: AuthService) => {
  return async (): Promise<AuthStore> => {
    // Load the configuration.
    await configurationService.load();
    // Refresh the token.
    return lastValueFrom(authService.refreshToken());
  };
};

function delayInitialLoading(delay = 100): () => Promise<void> {
  return (): Promise<void> => new Promise<void>(resolve => setTimeout(resolve, delay));
}

// AoT requires an exported function for factories
function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor]), withInterceptorsFromDi()),
    provideLoadingBarInterceptor(),
    {
      provide: UrlSerializer,
      useClass: TessUrlSerializer,
    },
    provideAppInitializer(() => appInit(inject(ConfigurationService), inject(AuthService))()),
    provideAppInitializer(() => delayInitialLoading()()),
    provideEnvironmentInitializer(() => inject(IconRepositoryService)),
    MAT_SORT_HEADER_INTL_PROVIDER,
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true } },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill', floatLabel: 'always' },
    },
    importProvidersFrom(
      // Translations.
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    provideNgxMask(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    {
      provide: SHARED_ASSETS_PATH,
      useValue: './shared/assets',
    },
  ],
};
